import {
  DesktopCloseResponseMessageSchema,
  DesktopCloseResultSchema,
  DesktopOperationResponseMessageSchema,
  DesktopOperationResultSchema,
  DESKTOP_PROTOCOL_VERSION,
  desktopAuthorizationOperationName,
  type DesktopFrameMessage,
  type DesktopOperationResult,
} from "../../shared/schemas";
import type { ActiveAppFrame } from "../app-frames";
import {
  closeError,
  correlateResult,
  DESKTOP_FRAME_BROKER_DEADLINES_MS,
  DesktopFrameDeadlineError,
  operationError,
  type DesktopPreloadApi,
  type OwnedSession,
  withDeadline,
} from "./broker-core";

type OperationMessage = Extract<
  DesktopFrameMessage,
  { type: "platform:desktop-operation-request" }
>;
type CloseMessage = Extract<DesktopFrameMessage, { type: "platform:desktop-capability-close" }>;

export async function performDesktopOperation(input: {
  active: ActiveAppFrame;
  api: DesktopPreloadApi | null;
  frameIsCurrent: () => boolean;
  message: OperationMessage;
  owned: OwnedSession;
  source: WindowProxy;
  onStale: () => Promise<void>;
}): Promise<void> {
  const { active, api, message, owned, source } = input;
  const operationName = desktopAuthorizationOperationName(message.operation);
  if (!owned.allowedOperations.includes(operationName) && operationName !== "disconnect") {
    postOperationResult(
      source,
      active,
      message,
      operationError(
        "OPERATION_DENIED",
        "That operation is not authorized for this session.",
        message.requestId,
      ),
    );
    return;
  }
  const rawResult = await requestOperation(api, message);
  if (!input.frameIsCurrent()) {
    await input.onStale();
    return;
  }
  const parsed = DesktopOperationResultSchema.safeParse(rawResult);
  postOperationResult(
    source,
    active,
    message,
    parsed.success
      ? correlateResult(parsed.data, message.requestId)
      : operationError(
          "INTERNAL_ERROR",
          "The desktop operation could not be completed.",
          message.requestId,
        ),
  );
}

async function requestOperation(
  api: DesktopPreloadApi | null,
  message: OperationMessage,
): Promise<unknown> {
  try {
    const operation = api?.performOperation({
      protocolVersion: DESKTOP_PROTOCOL_VERSION,
      sessionHandle: message.sessionHandle,
      operation: message.operation,
    });
    if (!operation) throw new Error("Desktop bridge unavailable");
    const interactive = message.operation.type === "filesystem.pick";
    return await withDeadline(
      operation,
      interactive
        ? DESKTOP_FRAME_BROKER_DEADLINES_MS.interactiveOperation
        : DESKTOP_FRAME_BROKER_DEADLINES_MS.operation,
      {
        code: "DESKTOP_TIMEOUT",
        message: interactive
          ? "CCBox Desktop did not finish the file selection in time."
          : "CCBox Desktop did not finish the local operation in time.",
      },
    );
  } catch (error) {
    return error instanceof DesktopFrameDeadlineError
      ? operationError(error.code, error.message, message.requestId, true)
      : null;
  }
}

function postOperationResult(
  source: WindowProxy,
  active: ActiveAppFrame,
  message: OperationMessage,
  result: DesktopOperationResult,
): void {
  source.postMessage(
    DesktopOperationResponseMessageSchema.parse({
      type: "platform:desktop-operation-response",
      protocolVersion: DESKTOP_PROTOCOL_VERSION,
      requestId: message.requestId,
      result,
    }),
    active.origin,
  );
}

export async function closeDesktopCapability(input: {
  active: ActiveAppFrame;
  api: DesktopPreloadApi | null;
  frameIsCurrent: () => boolean;
  message: CloseMessage;
  source: WindowProxy;
}): Promise<void> {
  const { active, message, source } = input;
  const rawClose = await requestClose(input.api, message);
  if (!input.frameIsCurrent()) return;
  const parsed = DesktopCloseResultSchema.safeParse(rawClose);
  const result = parsed.success
    ? correlateResult(parsed.data, message.requestId)
    : closeError(
        "INTERNAL_ERROR",
        "The desktop capability could not be closed.",
        message.requestId,
      );
  source.postMessage(
    DesktopCloseResponseMessageSchema.parse({
      type: "platform:desktop-capability-close-response",
      protocolVersion: DESKTOP_PROTOCOL_VERSION,
      requestId: message.requestId,
      result,
    }),
    active.origin,
  );
}

async function requestClose(
  api: DesktopPreloadApi | null,
  message: CloseMessage,
): Promise<unknown> {
  try {
    const close = api?.closeCapability({
      protocolVersion: DESKTOP_PROTOCOL_VERSION,
      sessionHandle: message.sessionHandle,
    });
    if (!close) throw new Error("Desktop bridge unavailable");
    return await withDeadline(close, DESKTOP_FRAME_BROKER_DEADLINES_MS.close, {
      code: "DESKTOP_TIMEOUT",
      message: "CCBox Desktop did not finish closing the local capability in time.",
    });
  } catch (error) {
    return error instanceof DesktopFrameDeadlineError
      ? closeError(error.code, error.message, message.requestId, true)
      : null;
  }
}
